import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import {
  BarChart2, Users, Calendar, FileText, DollarSign, MessageSquare,
  BookOpen, TrendingUp, Loader, Plus, Trash2, Edit, Eye, Check,
  X, Download, Search, ChevronDown, Send, AlertCircle, Receipt,
  CreditCard, Settings, Mail, Shield, RefreshCw, ExternalLink, Copy, LogOut
} from 'lucide-react';
import axios from 'axios';

const AdminDashboard = () => {
  usePageTitle('Admin Dashboard');
  const { getAuthHeaders, user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const headers = getAuthHeaders();
  const API = process.env.REACT_APP_BACKEND_URL + '/api';

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart2 },
    { id: 'leads', label: 'Leads', icon: TrendingUp },
    { id: 'appointments', label: 'Appointments', icon: Calendar },
    { id: 'clients', label: 'Clients', icon: Users },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'invoices', label: 'Invoices', icon: Receipt },
    { id: 'revenue', label: 'Revenue', icon: DollarSign },
    { id: 'payments', label: 'Payment Requests', icon: CreditCard },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'email-logs', label: 'Email Logs', icon: Mail },
    { id: 'security', label: '2FA & Security', icon: Shield },
    { id: 'config', label: 'Config', icon: Settings },
    { id: 'blog', label: 'Blog', icon: BookOpen },
  ];

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div style={{ background: '#0B1F3A' }} className="px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Clickable Logo */}
            <a 
              href="https://mataxpreparerllc.com" 
              className="flex items-center gap-2 sm:gap-3 hover:opacity-90 transition-opacity"
              data-testid="admin-logo-link"
            >
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#D4AF37' }}>
                <Shield size={20} className="text-[#0B1F3A]" />
              </div>
              <div className="hidden sm:block">
                <div className="font-heading font-bold text-white text-sm leading-none">MaTaxPreparer</div>
                <div className="text-[10px] text-[#D4AF37] tracking-wide">LLC</div>
              </div>
            </a>
            <div className="hidden sm:block h-8 w-px bg-white/20"></div>
            <div>
              <h1 className="font-heading font-bold text-white text-base sm:text-lg">Admin Dashboard</h1>
              <p className="text-blue-300 text-xs sm:text-sm">Control Center</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {user && (
              <span className="text-blue-200 text-sm hidden sm:block">
                {user.first_name} {user.last_name}
              </span>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex overflow-x-auto gap-1 py-3">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all duration-200 ${activeTab === id ? 'bg-[#0B1F3A] text-white' : 'text-[#475569] hover:bg-[#F8FAFC]'}`}
                data-testid={`admin-tab-${id}`}
              >
                <Icon size={16} /> {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'overview' && <OverviewTab API={API} headers={headers} setActiveTab={setActiveTab} />}
        {activeTab === 'leads' && <LeadsTab API={API} headers={headers} />}
        {activeTab === 'appointments' && <AdminAppointmentsTab API={API} headers={headers} />}
        {activeTab === 'clients' && <ClientsTab API={API} headers={headers} />}
        {activeTab === 'documents' && <AdminDocumentsTab API={API} headers={headers} />}
        {activeTab === 'invoices' && <InvoicesTab API={API} headers={headers} />}
        {activeTab === 'revenue' && <RevenueAnalyticsTab API={API} headers={headers} />}
        {activeTab === 'payments' && <PaymentRequestsTab API={API} headers={headers} />}
        {activeTab === 'messages' && <AdminMessagesTab API={API} headers={headers} />}
        {activeTab === 'email-logs' && <EmailLogsTab API={API} headers={headers} />}
        {activeTab === 'security' && <SecurityTab API={API} headers={headers} />}
        {activeTab === 'config' && <ConfigStatusTab API={API} headers={headers} />}
        {activeTab === 'blog' && <BlogTab API={API} headers={headers} />}
      </div>
    </div>
  );
};

// ─── Overview ────────────────────────────────────────────
const OverviewTab = ({ API, headers, setActiveTab }) => {
  const [analytics, setAnalytics] = useState(null);
  const [badges, setBadges] = useState({});

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/analytics`, { headers }),
      axios.get(`${API}/admin/notification-badges`, { headers }).catch(() => ({ data: {} }))
    ]).then(([analyticsRes, badgesRes]) => {
      setAnalytics(analyticsRes.data);
      setBadges(badgesRes.data);
    });
  }, [API, headers]);

  if (!analytics) return <div className="text-center py-20 text-[#94A3B8]"><Loader size={24} className="animate-spin mx-auto" /></div>;

  const cards = [
    { label: 'Total Clients', value: analytics.total_clients, icon: Users, color: '#0B1F3A', tab: 'clients', badge: 0 },
    { label: 'Total Leads', value: analytics.total_leads, icon: TrendingUp, color: '#2563EB', sub: `${analytics.new_leads_this_month} this month`, tab: 'leads', badge: badges.new_leads || 0 },
    { label: 'Upcoming Appts', value: analytics.upcoming_appointments, icon: Calendar, color: '#10B981', tab: 'appointments', badge: badges.pending_appointments || 0 },
    { label: 'Total Revenue', value: `$${(analytics.total_revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, icon: DollarSign, color: '#D4AF37', sub: `$${(analytics.monthly_revenue || 0).toFixed(2)} this month`, tab: 'revenue', badge: 0 },
    { label: 'Documents', value: analytics.total_documents, icon: FileText, color: '#0B1F3A', sub: `${analytics.pending_documents} pending`, tab: 'documents', badge: badges.pending_documents || 0 },
    { label: 'Emails Sent', value: analytics.email_stats?.sent || 0, icon: Mail, color: '#2563EB', sub: `${analytics.email_stats?.failed || 0} failed`, tab: 'email-logs', badge: 0 },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold text-[#0B1F3A]">Business Overview</h2>
        <div className="flex items-center gap-3">
          {badges.new_leads > 0 && (
            <span className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold animate-pulse">
              {badges.new_leads} new lead{badges.new_leads > 1 ? 's' : ''}
            </span>
          )}
          {badges.unread_messages > 0 && (
            <span className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold animate-pulse">
              {badges.unread_messages} new message{badges.unread_messages > 1 ? 's' : ''}
            </span>
          )}
          {badges.pending_invoices > 0 && (
            <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
              {badges.pending_invoices} unpaid invoice{badges.pending_invoices > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map(({ label, value, icon: Icon, color, sub, tab, badge }) => (
          <button
            key={label}
            onClick={() => setActiveTab(tab)}
            className="relative bg-white rounded-2xl p-6 border border-gray-100 shadow-card text-left hover:shadow-lg hover:border-[#0B1F3A]/30 transition-all duration-200 group cursor-pointer"
            data-testid={`overview-tile-${tab}`}
          >
            {badge > 0 && (
              <span className="absolute -top-2 -right-2 min-w-[24px] h-6 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full px-2 animate-pulse">
                {badge}
              </span>
            )}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
                  <Icon size={20} style={{ color }} />
                </div>
                <div className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wide">{label}</div>
              </div>
              <ChevronDown size={16} className="text-gray-300 group-hover:text-[#0B1F3A] -rotate-90 group-hover:translate-x-1 transition-all" />
            </div>
            <div className="font-heading font-bold text-3xl text-[#0B1F3A]">{value}</div>
            {sub && <div className="text-xs text-[#94A3B8] mt-1">{sub}</div>}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-[#0B1F3A]">Recent Leads</h3>
            <button onClick={() => setActiveTab('leads')} className="text-xs text-[#2563EB] hover:underline flex items-center gap-1">
              View All <ChevronDown size={14} className="-rotate-90" />
            </button>
          </div>
          <div className="space-y-3">
            {(analytics.recent_leads || []).length === 0 ? (
              <div className="text-center text-[#94A3B8] py-8 text-sm">No leads yet</div>
            ) : (analytics.recent_leads || []).map((lead) => (
              <div key={lead.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div>
                  <div className="font-medium text-[#0B1F3A] text-sm">{lead.name}</div>
                  <div className="text-[#94A3B8] text-xs">{lead.email} · {lead.service_interest || 'General'}</div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${lead.status === 'new' ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>{lead.status}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-[#0B1F3A]">Recent Appointments</h3>
            <button onClick={() => setActiveTab('appointments')} className="text-xs text-[#2563EB] hover:underline flex items-center gap-1">
              View All <ChevronDown size={14} className="-rotate-90" />
            </button>
          </div>
          <div className="space-y-3">
            {(analytics.recent_appointments || []).length === 0 ? (
              <div className="text-center text-[#94A3B8] py-8 text-sm">No appointments yet</div>
            ) : (analytics.recent_appointments || []).map((appt) => (
              <div key={appt.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div>
                  <div className="font-medium text-[#0B1F3A] text-sm">{appt.client_name || 'Client'}</div>
                  <div className="text-[#94A3B8] text-xs">{appt.service_type} · {appt.date} {appt.time}</div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${appt.status === 'confirmed' ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'}`}>{appt.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Leads Tab ────────────────────────────────────────────
const LeadsTab = ({ API, headers }) => {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchLeads(); }, [filter]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const url = filter === 'all' ? `${API}/leads` : `${API}/leads?status=${filter}`;
      const res = await axios.get(url, { headers });
      // Handle both array and paginated response
      const data = res.data?.leads || res.data || [];
      setLeads(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch leads:', err);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/leads/${id}`, { status }, { headers });
    fetchLeads();
  };

  const handleExport = () => {
    window.open(`${API}/admin/export/leads`, '_blank');
  };

  if (loading) return <div className="text-center py-20"><Loader size={24} className="animate-spin mx-auto text-[#94A3B8]" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold text-[#0B1F3A]">Leads ({leads.length})</h2>
        <div className="flex gap-3">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all" data-testid="leads-filter">
            {['all', 'new', 'contacted', 'converted', 'lost'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          <button onClick={handleExport} className="h-10 px-4 rounded-xl border border-gray-200 text-sm text-[#475569] hover:bg-gray-50 flex items-center gap-2 transition-all duration-200"><Download size={16} /> Export CSV</button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
        {leads.length === 0 ? (
          <div className="p-12 text-center text-[#94A3B8]">No leads found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] border-b border-gray-100">
                <tr>
                  {['Name', 'Email', 'Phone', 'Service', 'Status', 'Date', 'Actions'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-[#F8FAFC]" data-testid="lead-row">
                    <td className="px-4 py-3 font-medium text-[#0B1F3A]">{lead.name}</td>
                    <td className="px-4 py-3 text-[#475569]">{lead.email}</td>
                    <td className="px-4 py-3 text-[#475569]">{lead.phone || '—'}</td>
                    <td className="px-4 py-3 text-[#475569]">{lead.service_interest || '—'}</td>
                    <td className="px-4 py-3">
                      <select value={lead.status} onChange={(e) => updateStatus(lead.id, e.target.value)} className="h-8 px-2 bg-white border border-gray-200 rounded-lg text-xs outline-none focus:border-[#0B1F3A] transition-all" data-testid="lead-status-select">
                        {['new', 'contacted', 'converted', 'lost'].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-[#94A3B8] text-xs">{new Date(lead.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <a href={`mailto:${lead.email}`} className="text-[#2563EB] hover:underline text-xs">Email</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Admin Appointments ────────────────────────────────────────────
const AdminAppointmentsTab = ({ API, headers }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/appointments`, { headers });
      // Handle both array and paginated response formats
      const data = res.data?.appointments || res.data?.items || (Array.isArray(res.data) ? res.data : []);
      setAppointments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch appointments:', err);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/appointments/${id}`, { status }, { headers });
      fetchAppointments();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update appointment');
    }
  };

  const statusBadge = (s) => {
    const map = { pending: 'bg-yellow-100 text-yellow-700', confirmed: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-700', completed: 'bg-gray-100 text-gray-600' };
    return map[s] || 'bg-gray-100 text-gray-600';
  };

  if (loading) return <div className="text-center py-20"><Loader size={24} className="animate-spin mx-auto text-[#94A3B8]" /></div>;

  return (
    <div className="space-y-4 animate-fadeIn">
      <h2 className="font-heading text-2xl font-bold text-[#0B1F3A]">Appointments ({appointments.length})</h2>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
        {appointments.length === 0 ? (
          <div className="p-12 text-center text-[#94A3B8]">
            <Calendar size={40} className="mx-auto mb-3 opacity-30" />
            <p>No appointments found</p>
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] border-b border-gray-100">
              <tr>{['Client', 'Service', 'Date', 'Time', 'Type', 'Status', 'Actions'].map(h => <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {appointments.map((appt) => (
                <tr key={appt.id} className="hover:bg-[#F8FAFC]" data-testid="admin-appointment-row">
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#0B1F3A]">{appt.client_name}</div>
                    <div className="text-xs text-[#94A3B8]">{appt.client_email}</div>
                  </td>
                  <td className="px-4 py-3 text-[#475569] text-xs">{appt.service_type}</td>
                  <td className="px-4 py-3 text-[#475569]">{appt.date}</td>
                  <td className="px-4 py-3 text-[#475569]">{appt.time}</td>
                  <td className="px-4 py-3 text-[#475569]">{appt.is_online ? 'Online' : 'In-Person'}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusBadge(appt.status)}`}>{appt.status}</span></td>
                  <td className="px-4 py-3">
                    <select value={appt.status} onChange={(e) => updateStatus(appt.id, e.target.value)} className="h-8 px-2 bg-white border border-gray-200 rounded-lg text-xs outline-none focus:border-[#0B1F3A]" data-testid="appt-status-select">
                      {['pending', 'confirmed', 'completed', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
};

// ─── Clients ────────────────────────────────────────────
const ClientsTab = ({ API, headers }) => {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState('info');
  const [downloadingDocId, setDownloadingDocId] = useState(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/clients`, { headers });
      const data = res.data?.clients || res.data || [];
      setClients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (client) => {
    setClientToDelete(client);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!clientToDelete) return;
    setDeletingId(clientToDelete.id);
    try {
      await axios.delete(`${API}/admin/clients/${clientToDelete.id}`, { headers });
      setShowDeleteModal(false);
      setClientToDelete(null);
      fetchClients();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete client');
    } finally {
      setDeletingId(null);
    }
  };

  // Fetch client details
  const handleViewClient = async (client) => {
    setSelectedClient(client);
    setLoadingDetails(true);
    setActiveDetailTab('info');
    try {
      const res = await axios.get(`${API}/admin/clients/${client.id}`, { headers });
      setClientDetails(res.data);
    } catch (err) {
      console.error('Failed to fetch client details:', err);
      setClientDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Download document from client detail view
  const handleDownloadDoc = async (doc) => {
    setDownloadingDocId(doc.id);
    try {
      const response = await axios.get(`${API}/documents/${doc.id}/download`, { headers });
      const { filename, file_type, file_data } = response.data;
      
      if (!file_data) throw new Error('File not available');
      
      const base64Data = file_data.includes(',') ? file_data.split(',')[1] : file_data;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: file_type || 'application/octet-stream' });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDownloadingDocId(null);
    }
  };

  const filtered = clients.filter(c =>
    !search || `${c.first_name} ${c.last_name} ${c.email}`.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="text-center py-20"><Loader size={24} className="animate-spin mx-auto text-[#94A3B8]" /></div>;

  return (
    <div className="space-y-4 animate-fadeIn">
      {/* Delete Confirmation Modal */}
      {showDeleteModal && clientToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteModal(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full animate-fadeIn" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle size={24} className="text-red-500" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-[#0B1F3A]">Delete Client</h3>
                <p className="text-sm text-[#94A3B8]">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-[#475569] mb-4">
              Are you sure you want to delete <strong>{clientToDelete.first_name} {clientToDelete.last_name}</strong> ({clientToDelete.email})?
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <p className="text-amber-700 text-sm">
                <strong>Note:</strong> Clients with unpaid invoices cannot be deleted. Related documents and appointments will be marked as orphaned.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-[#475569] font-semibold text-sm hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deletingId === clientToDelete.id}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white font-semibold text-sm hover:bg-red-600 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="confirm-delete-client-btn"
              >
                {deletingId === clientToDelete.id ? <Loader size={16} className="animate-spin" /> : <Trash2 size={16} />}
                {deletingId === clientToDelete.id ? 'Deleting...' : 'Delete Client'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Client Detail Modal */}
      {selectedClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedClient(null)}>
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-fadeIn" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-[#F8FAFC]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg text-white" style={{ background: '#0B1F3A' }}>
                  {selectedClient.first_name?.charAt(0)}
                </div>
                <div>
                  <h3 className="font-heading font-bold text-[#0B1F3A]">{selectedClient.first_name} {selectedClient.last_name}</h3>
                  <p className="text-sm text-[#94A3B8]">{selectedClient.email}</p>
                </div>
              </div>
              <button onClick={() => setSelectedClient(null)} className="p-2 hover:bg-gray-200 rounded-lg">
                <X size={20} className="text-[#94A3B8]" />
              </button>
            </div>

            {/* Detail Tabs */}
            <div className="flex border-b border-gray-100 overflow-x-auto">
              {[
                { id: 'info', label: 'Info', icon: Users },
                { id: 'documents', label: 'Documents', icon: FileText },
                { id: 'messages', label: 'Messages', icon: MessageSquare },
                { id: 'invoices', label: 'Invoices', icon: Receipt },
                { id: 'appointments', label: 'Appointments', icon: Calendar },
                { id: 'payments', label: 'Payments', icon: CreditCard },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveDetailTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeDetailTab === tab.id
                      ? 'border-[#0B1F3A] text-[#0B1F3A]'
                      : 'border-transparent text-[#94A3B8] hover:text-[#475569]'
                  }`}
                >
                  <tab.icon size={16} />
                  {tab.label}
                  {clientDetails && tab.id !== 'info' && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-gray-100">
                      {tab.id === 'documents' && clientDetails.documents?.length}
                      {tab.id === 'messages' && clientDetails.messages?.length}
                      {tab.id === 'invoices' && clientDetails.invoices?.length}
                      {tab.id === 'appointments' && clientDetails.appointments?.length}
                      {tab.id === 'payments' && (clientDetails.payment_requests?.length || 0) + (clientDetails.transactions?.length || 0)}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
              {loadingDetails ? (
                <div className="flex items-center justify-center py-20">
                  <Loader size={32} className="animate-spin text-[#94A3B8]" />
                </div>
              ) : !clientDetails ? (
                <div className="text-center py-20 text-[#94A3B8]">Failed to load client details</div>
              ) : (
                <>
                  {/* Info Tab */}
                  {activeDetailTab === 'info' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-[#F8FAFC] rounded-xl p-4">
                          <label className="text-xs text-[#94A3B8] uppercase tracking-wide">Full Name</label>
                          <p className="font-medium text-[#0B1F3A] mt-1">{clientDetails.client?.first_name} {clientDetails.client?.last_name}</p>
                        </div>
                        <div className="bg-[#F8FAFC] rounded-xl p-4">
                          <label className="text-xs text-[#94A3B8] uppercase tracking-wide">Email</label>
                          <p className="font-medium text-[#0B1F3A] mt-1">{clientDetails.client?.email}</p>
                        </div>
                        <div className="bg-[#F8FAFC] rounded-xl p-4">
                          <label className="text-xs text-[#94A3B8] uppercase tracking-wide">Phone</label>
                          <p className="font-medium text-[#0B1F3A] mt-1">{clientDetails.client?.phone || '—'}</p>
                        </div>
                        <div className="bg-[#F8FAFC] rounded-xl p-4">
                          <label className="text-xs text-[#94A3B8] uppercase tracking-wide">Joined</label>
                          <p className="font-medium text-[#0B1F3A] mt-1">{clientDetails.client?.created_at ? new Date(clientDetails.client.created_at).toLocaleDateString() : '—'}</p>
                        </div>
                        <div className="bg-[#F8FAFC] rounded-xl p-4">
                          <label className="text-xs text-[#94A3B8] uppercase tracking-wide">Status</label>
                          <p className="mt-1">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${clientDetails.client?.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {clientDetails.client?.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </p>
                        </div>
                        <div className="bg-[#F8FAFC] rounded-xl p-4">
                          <label className="text-xs text-[#94A3B8] uppercase tracking-wide">2FA Enabled</label>
                          <p className="mt-1">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${clientDetails.client?.two_factor_enabled ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                              {clientDetails.client?.two_factor_enabled ? 'Yes' : 'No'}
                            </span>
                          </p>
                        </div>
                      </div>
                      
                      {/* Summary Cards */}
                      <div className="grid grid-cols-3 gap-4 mt-6">
                        <div className="bg-green-50 rounded-xl p-4 text-center">
                          <p className="text-2xl font-bold text-green-700">${clientDetails.summary?.total_paid?.toFixed(2) || '0.00'}</p>
                          <p className="text-xs text-green-600 mt-1">Total Paid</p>
                        </div>
                        <div className="bg-amber-50 rounded-xl p-4 text-center">
                          <p className="text-2xl font-bold text-amber-700">${clientDetails.summary?.pending_amount?.toFixed(2) || '0.00'}</p>
                          <p className="text-xs text-amber-600 mt-1">Pending</p>
                        </div>
                        <div className="bg-blue-50 rounded-xl p-4 text-center">
                          <p className="text-2xl font-bold text-blue-700">{clientDetails.summary?.total_documents || 0}</p>
                          <p className="text-xs text-blue-600 mt-1">Documents</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Documents Tab */}
                  {activeDetailTab === 'documents' && (
                    <div>
                      {clientDetails.documents?.length === 0 ? (
                        <div className="text-center py-12 text-[#94A3B8]">
                          <FileText size={40} className="mx-auto mb-3 opacity-30" />
                          <p>No documents uploaded</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {clientDetails.documents?.map(doc => (
                            <div key={doc.id} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-xl hover:bg-gray-100 transition-colors">
                              <div className="flex items-center gap-3">
                                <FileText size={20} className="text-[#94A3B8]" />
                                <div>
                                  <p className="font-medium text-[#0B1F3A] text-sm">{doc.original_filename}</p>
                                  <p className="text-xs text-[#94A3B8]">{doc.category} · {new Date(doc.uploaded_at).toLocaleDateString()}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${doc.status === 'pending_review' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                                  {doc.status === 'pending_review' ? 'Pending' : 'Reviewed'}
                                </span>
                                <button
                                  onClick={() => handleDownloadDoc(doc)}
                                  disabled={downloadingDocId === doc.id}
                                  className="p-2 hover:bg-white rounded-lg transition-colors disabled:opacity-60"
                                  title="Download"
                                >
                                  {downloadingDocId === doc.id ? <Loader size={14} className="animate-spin" /> : <Download size={14} className="text-[#475569]" />}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Messages Tab */}
                  {activeDetailTab === 'messages' && (
                    <div>
                      {clientDetails.messages?.length === 0 ? (
                        <div className="text-center py-12 text-[#94A3B8]">
                          <MessageSquare size={40} className="mx-auto mb-3 opacity-30" />
                          <p>No messages</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {clientDetails.messages?.map(msg => (
                            <div key={msg.id} className={`p-4 rounded-xl ${msg.sender_role === 'client' ? 'bg-blue-50 ml-0 mr-8' : 'bg-gray-50 ml-8 mr-0'}`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className={`text-xs font-semibold ${msg.sender_role === 'client' ? 'text-blue-600' : 'text-[#475569]'}`}>
                                  {msg.sender_name || (msg.sender_role === 'client' ? 'Client' : 'Admin')}
                                </span>
                                <span className="text-xs text-[#94A3B8]">{new Date(msg.created_at).toLocaleString()}</span>
                              </div>
                              <p className="font-medium text-[#0B1F3A] text-sm">{msg.subject}</p>
                              <p className="text-sm text-[#475569] mt-1">{msg.body}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Invoices Tab */}
                  {activeDetailTab === 'invoices' && (
                    <div>
                      {clientDetails.invoices?.length === 0 ? (
                        <div className="text-center py-12 text-[#94A3B8]">
                          <Receipt size={40} className="mx-auto mb-3 opacity-30" />
                          <p>No invoices</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {clientDetails.invoices?.map(inv => (
                            <div key={inv.id} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-xl hover:bg-gray-100 transition-colors">
                              <div>
                                <p className="font-medium text-[#0B1F3A] text-sm">{inv.invoice_number}</p>
                                <p className="text-xs text-[#94A3B8]">{inv.invoice_type} · {new Date(inv.created_at).toLocaleDateString()}</p>
                                {inv.due_date && <p className="text-xs text-[#94A3B8]">Due: {inv.due_date}</p>}
                              </div>
                              <div className="flex items-center gap-3">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                  inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                                  inv.status === 'overdue' ? 'bg-red-100 text-red-700' :
                                  inv.status === 'sent' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {inv.status}
                                </span>
                                <span className="font-bold text-[#0B1F3A]">${inv.total?.toFixed(2)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Appointments Tab */}
                  {activeDetailTab === 'appointments' && (
                    <div>
                      {clientDetails.appointments?.length === 0 ? (
                        <div className="text-center py-12 text-[#94A3B8]">
                          <Calendar size={40} className="mx-auto mb-3 opacity-30" />
                          <p>No appointments</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {clientDetails.appointments?.map(apt => (
                            <div key={apt.id} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-xl">
                              <div>
                                <p className="font-medium text-[#0B1F3A] text-sm">{apt.service_type}</p>
                                <p className="text-xs text-[#94A3B8]">{apt.date} at {apt.time}</p>
                              </div>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                apt.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                                apt.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {apt.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Payments Tab */}
                  {activeDetailTab === 'payments' && (
                    <div>
                      {(clientDetails.payment_requests?.length === 0 && clientDetails.transactions?.length === 0) ? (
                        <div className="text-center py-12 text-[#94A3B8]">
                          <CreditCard size={40} className="mx-auto mb-3 opacity-30" />
                          <p>No payment history</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {clientDetails.payment_requests?.length > 0 && (
                            <div>
                              <h4 className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wide mb-2">Payment Requests</h4>
                              <div className="space-y-2">
                                {clientDetails.payment_requests?.map(pr => (
                                  <div key={pr.id} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-xl">
                                    <div>
                                      <p className="font-medium text-[#0B1F3A] text-sm">{pr.service_description}</p>
                                      <p className="text-xs text-[#94A3B8]">{new Date(pr.created_at).toLocaleDateString()}</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                        pr.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                      }`}>
                                        {pr.status}
                                      </span>
                                      <span className="font-bold text-[#0B1F3A]">${pr.amount?.toFixed(2)}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {clientDetails.transactions?.length > 0 && (
                            <div>
                              <h4 className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wide mb-2">Transactions</h4>
                              <div className="space-y-2">
                                {clientDetails.transactions?.map(tx => (
                                  <div key={tx.id} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-xl">
                                    <div>
                                      <p className="font-medium text-[#0B1F3A] text-sm">{tx.invoice_number || 'Payment'}</p>
                                      <p className="text-xs text-[#94A3B8]">{new Date(tx.created_at).toLocaleDateString()}</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                        tx.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                      }`}>
                                        {tx.payment_status}
                                      </span>
                                      <span className="font-bold text-[#0B1F3A]">${tx.amount?.toFixed(2)}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold text-[#0B1F3A]">Clients ({clients.length})</h2>
        <div className="flex gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clients..." className="h-10 pl-9 pr-4 bg-white border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all" data-testid="client-search-input" />
          </div>
          <button onClick={() => window.open(`${API}/admin/export/clients`, '_blank')} className="h-10 px-4 rounded-xl border border-gray-200 text-sm text-[#475569] hover:bg-gray-50 flex items-center gap-2">
            <Download size={16} /> Export
          </button>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-[#94A3B8]">
            <Users size={40} className="mx-auto mb-3 opacity-30" />
            <p>No clients found</p>
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] border-b border-gray-100">
              <tr>{['Name', 'Email', 'Phone', 'Joined', 'Status', 'Actions'].map(h => <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((client) => (
                <tr key={client.id} className="hover:bg-[#F8FAFC]" data-testid="client-row">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleViewClient(client)}
                      className="flex items-center gap-2 group"
                      data-testid="view-client-btn"
                    >
                      <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs text-white" style={{ background: '#0B1F3A' }}>{client.first_name?.charAt(0)}</div>
                      <span className="font-medium text-[#0B1F3A] group-hover:text-[#2563EB] group-hover:underline transition-colors">{client.first_name} {client.last_name}</span>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-[#475569]">{client.email}</td>
                  <td className="px-4 py-3 text-[#475569]">{client.phone || '—'}</td>
                  <td className="px-4 py-3 text-[#94A3B8] text-xs">{new Date(client.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${client.is_active ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>{client.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewClient(client)}
                        className="p-2 hover:bg-blue-50 rounded-lg transition-colors group"
                        title="View details"
                        data-testid="view-client-details-btn"
                      >
                        <Eye size={16} className="text-gray-400 group-hover:text-blue-500 transition-colors" />
                      </button>
                      <button
                        onClick={() => confirmDelete(client)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
                        title="Delete client"
                        data-testid="delete-client-btn"
                      >
                        <Trash2 size={16} className="text-gray-400 group-hover:text-red-500 transition-colors" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
};

// ─── Documents ────────────────────────────────────────────
const AdminDocumentsTab = ({ API, headers }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/documents`, { headers })
      .then(r => {
        const data = r.data?.documents || r.data || [];
        setDocs(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error('Failed to fetch documents:', err);
        setDocs([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/documents/${id}`, { status }, { headers });
    axios.get(`${API}/documents`, { headers }).then(r => {
      const data = r.data?.documents || r.data || [];
      setDocs(Array.isArray(data) ? data : []);
    });
  };

  // Download document
  const handleDownload = async (doc) => {
    setDownloadingId(doc.id);
    try {
      const response = await axios.get(`${API}/documents/${doc.id}/download`, { headers });
      const { filename, file_type, file_data } = response.data;
      
      if (!file_data) {
        throw new Error('File data not available');
      }
      
      // Remove data URL prefix if present
      const base64Data = file_data.includes(',') ? file_data.split(',')[1] : file_data;
      
      // Convert base64 to blob
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: file_type || 'application/octet-stream' });
      
      // Download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download: ' + (err.response?.data?.detail || err.message || 'File not available'));
    } finally {
      setDownloadingId(null);
    }
  };

  // Preview document
  const handlePreview = async (doc) => {
    setPreviewError(null);
    setPreviewDoc({ ...doc, loading: true });
    try {
      const response = await axios.get(`${API}/documents/${doc.id}/download`, { headers });
      const { filename, file_type, file_data } = response.data;
      
      if (!file_data) {
        throw new Error('File data not available');
      }
      
      setPreviewDoc({
        ...doc,
        filename,
        file_type,
        file_data,
        loading: false
      });
    } catch (err) {
      console.error('Preview failed:', err);
      setPreviewError(err.response?.data?.detail || err.message || 'Failed to load file');
      setPreviewDoc({ ...doc, loading: false });
    }
  };

  // Get file icon based on type
  const getFileIcon = (fileType) => {
    if (!fileType) return FileText;
    if (fileType.includes('pdf')) return FileText;
    if (fileType.includes('image')) return Eye;
    return FileText;
  };

  // Check if file can be previewed
  const canPreview = (fileType) => {
    if (!fileType) return false;
    return fileType.includes('pdf') || fileType.includes('image');
  };

  if (loading) return <div className="text-center py-20"><Loader size={24} className="animate-spin mx-auto text-[#94A3B8]" /></div>;

  return (
    <div className="space-y-4 animate-fadeIn">
      <h2 className="font-heading text-2xl font-bold text-[#0B1F3A]">Documents ({docs.length})</h2>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
        {docs.length === 0 ? (
          <div className="p-12 text-center text-[#94A3B8]">No documents found.</div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] border-b border-gray-100">
              <tr>{['Client', 'Document', 'Category', 'Uploaded', 'Status', 'Actions'].map(h => <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {docs.map((doc) => {
                const FileIcon = getFileIcon(doc.file_type);
                return (
                <tr key={doc.id} className="hover:bg-[#F8FAFC]" data-testid="admin-doc-row">
                  <td className="px-4 py-3"><div className="font-medium text-[#0B1F3A]">{doc.client_name}</div><div className="text-xs text-[#94A3B8]">{doc.client_email}</div></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <FileIcon size={16} className="text-[#94A3B8]" />
                      <span className="text-[#475569] text-xs max-w-xs truncate">{doc.original_filename}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#475569] text-xs">{doc.category}</td>
                  <td className="px-4 py-3 text-[#94A3B8] text-xs">{new Date(doc.uploaded_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${doc.status === 'pending_review' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                      {doc.status === 'pending_review' ? 'Pending' : 'Reviewed'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {/* View Button */}
                      {canPreview(doc.file_type) && (
                        <button
                          onClick={() => handlePreview(doc)}
                          className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-[#475569] hover:bg-gray-50 transition-colors flex items-center gap-1"
                          title="Preview"
                          data-testid="view-doc-btn"
                        >
                          <Eye size={12} /> View
                        </button>
                      )}
                      {/* Download Button */}
                      <button
                        onClick={() => handleDownload(doc)}
                        disabled={downloadingId === doc.id}
                        className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-[#475569] hover:bg-gray-50 transition-colors flex items-center gap-1 disabled:opacity-60"
                        title="Download"
                        data-testid="download-doc-btn"
                      >
                        {downloadingId === doc.id ? <Loader size={12} className="animate-spin" /> : <Download size={12} />}
                        {downloadingId === doc.id ? '...' : 'Download'}
                      </button>
                      {/* Mark Reviewed */}
                      {doc.status === 'pending_review' && (
                        <button onClick={() => updateStatus(doc.id, 'reviewed')} className="text-xs px-2.5 py-1.5 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors" data-testid="review-doc-btn">
                          <Check size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* Document Preview Modal */}
      {previewDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setPreviewDoc(null)}>
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-fadeIn" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div>
                <h3 className="font-heading font-bold text-[#0B1F3A]">{previewDoc.original_filename}</h3>
                <p className="text-xs text-[#94A3B8]">{previewDoc.category} · {previewDoc.client_name}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownload(previewDoc)}
                  disabled={downloadingId === previewDoc.id}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-[#475569] hover:bg-gray-50 flex items-center gap-1 disabled:opacity-60"
                >
                  <Download size={14} /> Download
                </button>
                <button onClick={() => setPreviewDoc(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X size={20} className="text-[#94A3B8]" />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
              {previewDoc.loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader size={32} className="animate-spin text-[#94A3B8]" />
                </div>
              ) : previewError ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <AlertCircle size={48} className="text-red-400 mb-4" />
                  <p className="text-red-600 font-medium">File not available</p>
                  <p className="text-[#94A3B8] text-sm mt-2">{previewError}</p>
                </div>
              ) : previewDoc.file_type?.includes('pdf') ? (
                <iframe
                  src={previewDoc.file_data}
                  className="w-full rounded-lg border border-gray-200"
                  style={{ height: '70vh' }}
                  title="PDF Preview"
                />
              ) : previewDoc.file_type?.includes('image') ? (
                <div className="flex items-center justify-center">
                  <img
                    src={previewDoc.file_data}
                    alt={previewDoc.original_filename}
                    className="max-w-full max-h-[70vh] rounded-lg shadow-lg"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <FileText size={48} className="text-[#94A3B8] mb-4" />
                  <p className="text-[#475569]">Preview not available for this file type</p>
                  <p className="text-[#94A3B8] text-sm mt-2">Click Download to save the file</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Invoices Tab (Admin) ────────────────────────────────────────────
const InvoicesTab = ({ API, headers }) => {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    is_walkin: false,
    client_id: '',
    client_email: '',
    client_name: '',
    client_phone: '',
    client_address: '',
    invoice_type: 'Tax Return',
    items: [{ description: '', amount: '' }],
    notes: '',
    due_date: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [downloadingId, setDownloadingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [sendingId, setSendingId] = useState(null);

  const invoiceTypes = [
    'Tax Return',
    'Business Filing',
    'Payroll Services',
    'Notice Response',
    'Tax Planning',
    'Amendment',
    'Other'
  ];

  // Predefined message templates
  const messageTemplates = [
    { label: 'Select a template...', value: '' },
    { label: 'Work Completed - Payment Due', value: 'Your tax preparation work has been completed. Please make the payment at your earliest convenience to receive your final documents.' },
    { label: 'Thank You - Payment Reminder', value: 'Thank you for choosing MA Tax Preparer LLC. This is a friendly reminder that your payment is due. Please let us know if you have any questions.' },
    { label: 'Service Complete - Documents Ready', value: 'Your service has been completed successfully. Once payment is received, your documents will be available for download in your client portal.' },
    { label: 'Payment Due Within 30 Days', value: 'Payment is due within 30 days of the invoice date. If you have already made the payment, please disregard this message.' },
    { label: 'Installment Payment Available', value: 'If you need to set up a payment plan, please contact us to discuss available options. We are here to help.' },
    { label: 'Urgent - Past Due Notice', value: 'Your invoice is past due. Please make the payment immediately to avoid any late fees or service interruptions.' },
    { label: 'Business Filing Complete', value: 'Your business filing has been submitted successfully. Please remit payment to complete the process. Contact us for any questions.' },
    { label: 'Tax Season Special', value: 'Thank you for trusting us with your tax preparation needs this season. Please complete your payment to finalize your filing.' }
  ];

  useEffect(() => {
    fetchInvoices();
    fetchClients();
  }, []);

  const fetchInvoices = async () => {
    try {
      const res = await axios.get(`${API}/invoices`, { headers });
      setInvoices(res.data?.invoices || []);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/invoices/admin/clients`, { headers });
      setClients(res.data?.clients || []);
      setLeads(res.data?.leads || []);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    }
  };

  // Authenticated PDF download
  const handleDownloadPdf = async (invoice) => {
    setDownloadingId(invoice.id);
    try {
      const response = await fetch(`${API}/invoices/${invoice.id}/pdf`, {
        method: 'GET',
        headers: headers
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to download PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice-${invoice.invoice_number || invoice.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message || 'Failed to download PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { description: '', amount: '' }] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, field, val) => setForm(f => ({
    ...f,
    items: f.items.map((item, idx) => idx === i ? { ...item, [field]: val } : item)
  }));

  const handleClientSelect = (e) => {
    const value = e.target.value;
    if (!value) {
      setForm(f => ({ ...f, client_id: '', client_email: '', client_name: '', client_phone: '' }));
      return;
    }

    // Try to find in clients first
    const client = clients.find(c => c.id === value);
    if (client) {
      setForm(f => ({
        ...f,
        client_id: client.id,
        client_email: client.email,
        client_name: `${client.first_name} ${client.last_name}`.trim(),
        client_phone: client.phone || ''
      }));
    } else {
      // Check leads
      const lead = leads.find(l => l.id === value);
      if (lead) {
        setForm(f => ({
          ...f,
          client_id: lead.id,
          client_email: lead.email,
          client_name: lead.name,
          client_phone: lead.phone || ''
        }));
      }
    }
  };

  // Handle customer type toggle
  const handleCustomerTypeChange = (isWalkin) => {
    setForm(f => ({
      ...f,
      is_walkin: isWalkin,
      client_id: isWalkin ? '' : f.client_id,
      client_email: isWalkin ? '' : f.client_email,
      client_name: isWalkin ? '' : f.client_name,
      client_phone: isWalkin ? '' : f.client_phone,
      client_address: isWalkin ? '' : f.client_address
    }));
  };

  // Handle message template selection
  const handleTemplateSelect = (template) => {
    if (template) {
      setForm(f => ({ ...f, notes: template }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/invoices`, {
        is_walkin: form.is_walkin,
        client_id: form.is_walkin ? null : form.client_id,
        client_email: form.client_email,
        client_name: form.client_name,
        client_phone: form.client_phone || null,
        client_address: form.client_address || null,
        invoice_type: form.invoice_type,
        items: form.items.map(item => ({
          description: item.description,
          amount: parseFloat(item.amount) || 0
        })),
        notes: form.notes,
        due_date: form.due_date || null
      }, { headers });

      setShowForm(false);
      setForm({
        is_walkin: false,
        client_id: '',
        client_email: '',
        client_name: '',
        client_phone: '',
        client_address: '',
        invoice_type: 'Tax Return',
        items: [{ description: '', amount: '' }],
        notes: '',
        due_date: ''
      });
      fetchInvoices();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    if (!window.confirm('Send this invoice to the client via email?')) return;
    setSendingId(invoiceId);
    try {
      await axios.post(`${API}/invoices/${invoiceId}/send`, {}, { headers });
      fetchInvoices();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send invoice');
    } finally {
      setSendingId(null);
    }
  };

  // Preview invoice in modal
  const handlePreviewInvoice = (invoice) => {
    setPreviewInvoice(invoice);
    setShowPreviewModal(true);
  };

  // Print invoice
  const handlePrintInvoice = async (invoice) => {
    // Open PDF in new tab for printing
    try {
      const response = await fetch(`${API}/invoices/${invoice.id}/pdf`, {
        method: 'GET',
        headers: headers
      });
      if (!response.ok) throw new Error('Failed to load PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      printWindow.onload = () => {
        printWindow.print();
      };
    } catch (err) {
      alert('Failed to print invoice: ' + err.message);
    }
  };

  // Resend invoice email
  const handleResendInvoice = async (invoiceId) => {
    if (!window.confirm('Resend this invoice email to the client?')) return;
    setSendingId(invoiceId);
    try {
      await axios.post(`${API}/invoices/${invoiceId}/send`, {}, { headers });
      alert('Invoice email sent successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send invoice');
    } finally {
      setSendingId(null);
    }
  };

  const handleUpdateStatus = async (invoiceId, newStatus) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}`, { status: newStatus }, { headers });
      fetchInvoices();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update invoice');
    }
  };

  const confirmDeleteInvoice = (invoice) => {
    setInvoiceToDelete(invoice);
    setShowDeleteModal(true);
  };

  const handleDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    setDeletingId(invoiceToDelete.id);
    try {
      await axios.delete(`${API}/invoices/${invoiceToDelete.id}`, { headers });
      setShowDeleteModal(false);
      setInvoiceToDelete(null);
      fetchInvoices();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete invoice');
    } finally {
      setDeletingId(null);
    }
  };

  const total = form.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

  const filteredInvoices = filterStatus === 'all'
    ? invoices
    : invoices.filter(inv => inv.status === filterStatus);

  const statusCounts = {
    all: invoices.length,
    draft: invoices.filter(i => i.status === 'draft').length,
    sent: invoices.filter(i => i.status === 'sent').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    overdue: invoices.filter(i => i.status === 'overdue').length,
  };

  const statusBadge = (status) => {
    const map = {
      draft: 'bg-gray-100 text-gray-600',
      sent: 'bg-yellow-100 text-yellow-700',
      paid: 'bg-green-100 text-green-700',
      overdue: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-500',
    };
    return <span className={`px-3 py-1 rounded-full text-xs font-semibold ${map[status] || 'bg-gray-100 text-gray-600'}`}>{status}</span>;
  };

  return (
    <div className="space-y-4 animate-fadeIn">
      {/* Delete Confirmation Modal */}
      {showDeleteModal && invoiceToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteModal(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full animate-fadeIn" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle size={24} className="text-red-500" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-[#0B1F3A]">Delete Invoice</h3>
                <p className="text-sm text-[#94A3B8]">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-[#475569] mb-4">
              Are you sure you want to delete invoice <strong>{invoiceToDelete.invoice_number}</strong>?
            </p>
            {invoiceToDelete.status !== 'draft' && invoiceToDelete.status !== 'cancelled' && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                <p className="text-red-700 text-sm">
                  <strong>Warning:</strong> Only draft or cancelled invoices can be deleted. This invoice is "{invoiceToDelete.status}".
                </p>
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-[#475569] font-semibold text-sm hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteInvoice}
                disabled={deletingId === invoiceToDelete.id || (invoiceToDelete.status !== 'draft' && invoiceToDelete.status !== 'cancelled')}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white font-semibold text-sm hover:bg-red-600 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="confirm-delete-invoice-btn"
              >
                {deletingId === invoiceToDelete.id ? <Loader size={16} className="animate-spin" /> : <Trash2 size={16} />}
                {deletingId === invoiceToDelete.id ? 'Deleting...' : 'Delete Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#0B1F3A]">Invoices</h2>
          <p className="text-[#475569] text-sm">Create and manage client invoices</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn-primary text-sm px-5 py-2.5"
          data-testid="create-invoice-btn"
        >
          <Plus size={16} /> Create Invoice
        </button>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {['all', 'draft', 'sent', 'paid', 'overdue'].map(status => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filterStatus === status
                ? 'bg-[#0B1F3A] text-white'
                : 'bg-white text-[#475569] border border-gray-200 hover:border-[#0B1F3A]'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)} ({statusCounts[status]})
          </button>
        ))}
      </div>

      {/* Create Invoice Form */}
      {showForm && (
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-card animate-fadeIn">
          <h3 className="font-heading font-semibold text-[#0B1F3A] mb-5">New Invoice</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Customer Type Toggle */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-2">Customer Type</label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => handleCustomerTypeChange(false)}
                  className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                    !form.is_walkin 
                      ? 'border-[#0B1F3A] bg-[#0B1F3A] text-white' 
                      : 'border-gray-200 bg-white text-[#475569] hover:border-gray-300'
                  }`}
                  data-testid="customer-type-registered"
                >
                  <Users size={18} />
                  <span className="font-medium">Registered Client</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleCustomerTypeChange(true)}
                  className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                    form.is_walkin 
                      ? 'border-[#0B1F3A] bg-[#0B1F3A] text-white' 
                      : 'border-gray-200 bg-white text-[#475569] hover:border-gray-300'
                  }`}
                  data-testid="customer-type-walkin"
                >
                  <Plus size={18} />
                  <span className="font-medium">Walk-in Customer</span>
                </button>
              </div>
              <p className="text-xs text-[#94A3B8] mt-2">
                {form.is_walkin 
                  ? "Enter customer details manually - no account required" 
                  : "Select from registered clients or leads in the system"}
              </p>
            </div>

            {/* Registered Client Selection */}
            {!form.is_walkin && (
              <div>
                <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Select Client</label>
                <select
                  value={form.client_id}
                  onChange={handleClientSelect}
                  className="w-full h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all"
                >
                  <option value="">-- Select a client --</option>
                  <optgroup label="Registered Clients">
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.first_name} {c.last_name} ({c.email})</option>
                    ))}
                  </optgroup>
                  <optgroup label="Leads">
                    {leads.map(l => (
                      <option key={l.id} value={l.id}>{l.name} ({l.email})</option>
                    ))}
                  </optgroup>
                </select>
              </div>
            )}

            {/* Customer Details - Editable for walk-ins, auto-filled for registered */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">
                  Customer Name *
                </label>
                <input
                  value={form.client_name}
                  onChange={(e) => setForm(f => ({ ...f, client_name: e.target.value }))}
                  required
                  placeholder="John Smith"
                  className="w-full h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all"
                  data-testid="invoice-client-name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">
                  Email *
                </label>
                <input
                  value={form.client_email}
                  onChange={(e) => setForm(f => ({ ...f, client_email: e.target.value }))}
                  required
                  type="email"
                  placeholder="customer@example.com"
                  className="w-full h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all"
                  data-testid="invoice-client-email"
                />
              </div>
            </div>

            {/* Additional fields for walk-in customers */}
            {form.is_walkin && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Phone</label>
                  <input
                    value={form.client_phone}
                    onChange={(e) => setForm(f => ({ ...f, client_phone: e.target.value }))}
                    placeholder="(770) 555-0123"
                    className="w-full h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all"
                    data-testid="invoice-client-phone"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Address</label>
                  <input
                    value={form.client_address}
                    onChange={(e) => setForm(f => ({ ...f, client_address: e.target.value }))}
                    placeholder="123 Main St, City, State ZIP"
                    className="w-full h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all"
                    data-testid="invoice-client-address"
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Invoice Type *</label>
                <select
                  value={form.invoice_type}
                  onChange={(e) => setForm(f => ({ ...f, invoice_type: e.target.value }))}
                  required
                  className="w-full h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all"
                  data-testid="invoice-type-select"
                >
                  {invoiceTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Due Date</label>
                <input
                  value={form.due_date}
                  onChange={(e) => setForm(f => ({ ...f, due_date: e.target.value }))}
                  type="date"
                  className="w-full h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-2">Line Items *</label>
              {form.items.map((item, i) => (
                <div key={i} className="flex gap-3 mb-2">
                  <input
                    value={item.description}
                    onChange={(e) => updateItem(i, 'description', e.target.value)}
                    required
                    placeholder="Service description"
                    className="flex-1 h-10 px-3 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all"
                  />
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] text-sm">$</span>
                    <input
                      value={item.amount}
                      onChange={(e) => updateItem(i, 'amount', e.target.value)}
                      required
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      className="w-32 h-10 pl-7 pr-3 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all"
                    />
                  </div>
                  {form.items.length > 1 && (
                    <button type="button" onClick={() => removeItem(i)} className="p-2 hover:bg-red-50 rounded-lg">
                      <X size={16} className="text-red-400" />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addItem} className="text-sm text-[#2563EB] hover:underline flex items-center gap-1 mt-2">
                <Plus size={14} /> Add Line Item
              </button>
              <div className="mt-4 p-4 bg-[#F8FAFC] rounded-xl flex justify-between items-center">
                <span className="font-semibold text-[#475569]">Total</span>
                <span className="font-heading font-bold text-2xl text-[#0B1F3A]">${total.toFixed(2)}</span>
              </div>
            </div>

            {/* Message Templates & Notes */}
            <div>
              <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Message Template</label>
              <select
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className="w-full h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all mb-2"
                data-testid="message-template-select"
              >
                {messageTemplates.map((t, i) => (
                  <option key={i} value={t.value}>{t.label}</option>
                ))}
              </select>
              <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Notes / Message to Customer</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Payment terms, special instructions, custom message..."
                rows={3}
                className="w-full px-4 py-3 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all resize-none"
                data-testid="invoice-notes"
              />
            </div>

            <div className="flex gap-4 pt-2">
              <button
                type="submit"
                disabled={submitting || total <= 0}
                className="btn-primary px-6 py-2.5 text-sm disabled:opacity-60"
                data-testid="submit-invoice-btn"
              >
                {submitting ? <><Loader size={14} className="animate-spin" /> Creating...</> : 'Create Invoice (Draft)'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-6 py-2.5 rounded-xl border border-gray-200 text-sm text-[#475569] hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Invoices List */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
        {filteredInvoices.length === 0 ? (
          <div className="p-12 text-center text-[#94A3B8]">
            <Receipt size={40} className="mx-auto mb-3 opacity-30" />
            <p>No invoices found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredInvoices.map((inv) => (
              <div key={inv.id} className="p-5" data-testid="invoice-row">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-semibold text-[#0B1F3A]">{inv.invoice_number}</span>
                      {statusBadge(inv.status)}
                    </div>
                    <div className="text-[#475569] text-sm">{inv.client_name}</div>
                    <div className="text-[#94A3B8] text-xs">{inv.client_email}</div>
                    <div className="text-[#94A3B8] text-xs mt-1">
                      {inv.invoice_type} · Created {new Date(inv.created_at).toLocaleDateString()}
                      {inv.due_date && ` · Due ${inv.due_date}`}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="font-heading font-bold text-xl text-[#0B1F3A]">${inv.total?.toFixed(2)}</div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap justify-end">
                      {/* Preview Button */}
                      <button
                        onClick={() => handlePreviewInvoice(inv)}
                        className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-[#475569] hover:bg-gray-50 transition-colors flex items-center gap-1"
                        data-testid="invoice-preview"
                      >
                        <Eye size={12} /> Preview
                      </button>
                      {/* PDF Download */}
                      <button
                        onClick={() => handleDownloadPdf(inv)}
                        disabled={downloadingId === inv.id}
                        className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-[#475569] hover:bg-gray-50 transition-colors flex items-center gap-1 disabled:opacity-60"
                        data-testid="invoice-download-pdf"
                      >
                        {downloadingId === inv.id ? <Loader size={12} className="animate-spin" /> : <Download size={12} />}
                        PDF
                      </button>
                      {/* Print Button */}
                      <button
                        onClick={() => handlePrintInvoice(inv)}
                        className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-[#475569] hover:bg-gray-50 transition-colors flex items-center gap-1"
                        data-testid="invoice-print"
                      >
                        <FileText size={12} /> Print
                      </button>
                      
                      {/* Status-specific actions */}
                      {inv.status === 'draft' && (
                        <>
                          <button
                            onClick={() => handleSendInvoice(inv.id)}
                            disabled={sendingId === inv.id}
                            className="text-xs px-3 py-1.5 rounded-lg bg-[#0B1F3A] text-white hover:bg-[#1E3A5F] transition-colors flex items-center gap-1 disabled:opacity-60"
                            data-testid="invoice-send"
                          >
                            {sendingId === inv.id ? <Loader size={12} className="animate-spin" /> : <Send size={12} />}
                            Send Email
                          </button>
                          <button
                            onClick={() => confirmDeleteInvoice(inv)}
                            className="text-xs px-3 py-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                            data-testid="delete-invoice-btn"
                          >
                            <Trash2 size={12} />
                          </button>
                        </>
                      )}
                      {inv.status === 'sent' && (
                        <>
                          <button
                            onClick={() => handleResendInvoice(inv.id)}
                            disabled={sendingId === inv.id}
                            className="text-xs px-3 py-1.5 rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors flex items-center gap-1 disabled:opacity-60"
                            data-testid="invoice-resend"
                          >
                            {sendingId === inv.id ? <Loader size={12} className="animate-spin" /> : <Mail size={12} />}
                            Resend
                          </button>
                          <button
                            onClick={() => handleUpdateStatus(inv.id, 'paid')}
                            className="text-xs px-3 py-1.5 rounded-lg bg-green-500 text-white hover:bg-green-600 transition-colors flex items-center gap-1"
                          >
                            <Check size={12} /> Mark Paid
                          </button>
                          <button
                            onClick={() => handleUpdateStatus(inv.id, 'overdue')}
                            className="text-xs px-3 py-1.5 rounded-lg text-orange-500 hover:bg-orange-50 transition-colors"
                          >
                            Overdue
                          </button>
                        </>
                      )}
                      {inv.status === 'overdue' && (
                        <>
                          <button
                            onClick={() => handleResendInvoice(inv.id)}
                            disabled={sendingId === inv.id}
                            className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors flex items-center gap-1 disabled:opacity-60"
                            data-testid="invoice-resend-overdue"
                          >
                            {sendingId === inv.id ? <Loader size={12} className="animate-spin" /> : <Mail size={12} />}
                            Resend
                          </button>
                          <button
                            onClick={() => handleUpdateStatus(inv.id, 'paid')}
                            className="text-xs px-3 py-1.5 rounded-lg bg-green-500 text-white hover:bg-green-600 transition-colors flex items-center gap-1"
                          >
                            <Check size={12} /> Mark Paid
                          </button>
                        </>
                      )}
                      {inv.status === 'cancelled' && (
                        <button
                          onClick={() => confirmDeleteInvoice(inv)}
                          className="text-xs px-3 py-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                          data-testid="delete-invoice-btn"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Line Items Preview */}
                {inv.items && inv.items.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex flex-wrap gap-2">
                      {inv.items.map((item, i) => (
                        <span key={i} className="text-xs bg-[#F8FAFC] px-2 py-1 rounded text-[#475569]">
                          {item.description}: ${item.amount?.toFixed(2)}
                        </span>
                      ))}
                    </div>
                    {inv.notes && (
                      <div className="text-xs text-[#94A3B8] mt-2 italic">Note: {inv.notes}</div>
                    )}
                    {inv.is_walkin && (
                      <span className="inline-block mt-2 text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">Walk-in Customer</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invoice Preview Modal */}
      {showPreviewModal && previewInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowPreviewModal(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-fadeIn" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between">
              <h3 className="font-heading font-bold text-[#0B1F3A]">Invoice Preview</h3>
              <button onClick={() => setShowPreviewModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} className="text-[#94A3B8]" />
              </button>
            </div>
            
            {/* Invoice Preview Content */}
            <div className="p-6">
              {/* Header with Flag Colors */}
              <div className="bg-gradient-to-r from-[#3C3B6E] to-[#0B1F3A] h-2 rounded-t-lg"></div>
              <div className="flex h-1">
                {[...Array(13)].map((_, i) => (
                  <div key={i} className={`flex-1 ${i % 2 === 0 ? 'bg-[#B22234]' : 'bg-white'}`}></div>
                ))}
              </div>
              
              {/* Company Header */}
              <div className="mt-4 flex justify-between items-start">
                <div>
                  <span className="text-xs text-[#3C3B6E] font-semibold">Authorized IRS e-file Provider</span>
                </div>
                <div className="text-right">
                  <h2 className="font-heading font-bold text-[#3C3B6E] text-lg">MA TAX PREPARER LLC</h2>
                  <p className="text-xs text-[#94A3B8]">Individual Tax Planning, Accounting and Tax</p>
                  <p className="text-xs text-[#0B1F3A] mt-1">4384 Laughlin Ct NW</p>
                  <p className="text-xs text-[#0B1F3A]">Kennesaw, GA 30144</p>
                  <p className="text-xs text-[#0B1F3A]">(770) 235-6999</p>
                </div>
              </div>
              
              <div className="h-0.5 bg-green-500 my-4"></div>
              
              {/* Invoice Title */}
              <h1 className="text-center font-heading font-bold text-3xl text-[#3C3B6E] mb-4">INVOICE</h1>
              
              {/* Invoice Details */}
              <div className="flex justify-between mb-6">
                <div>
                  <p className="text-sm"><span className="text-[#94A3B8]">Invoice #:</span> <strong>{previewInvoice.invoice_number}</strong></p>
                  <p className="text-sm"><span className="text-[#94A3B8]">Date:</span> <strong>{new Date(previewInvoice.created_at).toLocaleDateString()}</strong></p>
                  {previewInvoice.due_date && (
                    <p className="text-sm"><span className="text-[#94A3B8]">Due Date:</span> <strong>{previewInvoice.due_date}</strong></p>
                  )}
                </div>
                <div className={`px-4 py-2 rounded-lg border-2 ${
                  previewInvoice.status === 'paid' ? 'border-green-500 bg-green-50 text-green-700' :
                  previewInvoice.status === 'overdue' ? 'border-red-500 bg-red-50 text-red-700' :
                  previewInvoice.status === 'sent' ? 'border-yellow-500 bg-yellow-50 text-yellow-700' :
                  'border-gray-300 bg-gray-50 text-gray-600'
                }`}>
                  <span className="font-bold uppercase">{previewInvoice.status}</span>
                </div>
              </div>
              
              {/* Bill To */}
              <div className="bg-[#F8FAFC] rounded-xl p-4 mb-6">
                <h3 className="font-semibold text-[#3C3B6E] mb-2">BILL TO:</h3>
                <p className="font-bold text-[#0B1F3A]">{previewInvoice.client_name}</p>
                <p className="text-sm text-[#475569]">{previewInvoice.client_email}</p>
                {previewInvoice.client_phone && <p className="text-sm text-[#475569]">{previewInvoice.client_phone}</p>}
                {previewInvoice.client_address && <p className="text-sm text-[#475569]">{previewInvoice.client_address}</p>}
              </div>
              
              {/* Services Table */}
              <h3 className="font-semibold text-[#3C3B6E] mb-2">SERVICES</h3>
              <table className="w-full mb-6">
                <thead>
                  <tr className="bg-[#3C3B6E] text-white">
                    <th className="text-left p-3 rounded-tl-lg">Description</th>
                    <th className="text-right p-3 rounded-tr-lg">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {previewInvoice.items?.map((item, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="p-3 text-[#0B1F3A]">{item.description}</td>
                      <td className="p-3 text-right font-semibold text-[#0B1F3A]">${item.amount?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Total */}
              <div className="flex justify-end mb-6">
                <div className="bg-[#E0E7FF] px-6 py-3 rounded-lg border-2 border-[#3C3B6E]">
                  <span className="font-bold text-[#3C3B6E] text-xl">TOTAL: ${previewInvoice.total?.toFixed(2)} USD</span>
                </div>
              </div>
              
              {/* Notes */}
              {previewInvoice.notes && (
                <div className="bg-[#F8FAFC] rounded-xl p-4 mb-6">
                  <h3 className="font-semibold text-[#3C3B6E] mb-2">NOTES / MESSAGE</h3>
                  <p className="text-sm text-[#475569]">{previewInvoice.notes}</p>
                </div>
              )}
              
              {/* Footer */}
              <p className="text-center text-[#94A3B8] text-sm mb-4">Thank you for choosing MA Tax Preparer LLC!</p>
              <div className="bg-[#3C3B6E] text-white text-center py-2 rounded-lg text-sm">
                www.mataxpreparerllc.com | (770) 235-6999 | support@mataxpreparerllc.com
              </div>
            </div>
            
            {/* Modal Actions */}
            <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 flex gap-3 justify-end">
              <button
                onClick={() => handleDownloadPdf(previewInvoice)}
                disabled={downloadingId === previewInvoice.id}
                className="px-4 py-2 rounded-xl border border-gray-200 text-[#475569] hover:bg-gray-50 flex items-center gap-2 text-sm font-medium disabled:opacity-60"
              >
                {downloadingId === previewInvoice.id ? <Loader size={16} className="animate-spin" /> : <Download size={16} />}
                Download PDF
              </button>
              <button
                onClick={() => handlePrintInvoice(previewInvoice)}
                className="px-4 py-2 rounded-xl border border-gray-200 text-[#475569] hover:bg-gray-50 flex items-center gap-2 text-sm font-medium"
              >
                <FileText size={16} /> Print
              </button>
              {previewInvoice.status === 'draft' && (
                <button
                  onClick={() => { handleSendInvoice(previewInvoice.id); setShowPreviewModal(false); }}
                  className="px-4 py-2 rounded-xl bg-[#0B1F3A] text-white hover:bg-[#1E3A5F] flex items-center gap-2 text-sm font-medium"
                >
                  <Send size={16} /> Send to Client
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Admin Messages ────────────────────────────────────────────
const AdminMessagesTab = ({ API, headers }) => {
  const [messages, setMessages] = useState([]);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ recipient_id: '', subject: '', body: '' });
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [msgs, cls] = await Promise.all([
        axios.get(`${API}/messages`, { headers }),
        axios.get(`${API}/admin/clients`, { headers }),
      ]);
      // Handle both array and paginated response formats
      const messagesData = msgs.data?.messages || msgs.data?.items || (Array.isArray(msgs.data) ? msgs.data : []);
      const clientsData = cls.data?.clients || (Array.isArray(cls.data) ? cls.data : []);
      setMessages(Array.isArray(messagesData) ? messagesData : []);
      setClients(Array.isArray(clientsData) ? clientsData : []);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
      setMessages([]);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/messages`, form, { headers });
      setForm({ recipient_id: '', subject: '', body: '' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/messages/${id}/read`, {}, { headers });
      fetchData();
    } catch (err) {
      console.error('Failed to mark message as read:', err);
    }
  };

  if (loading) return <div className="text-center py-20"><Loader size={24} className="animate-spin mx-auto text-[#94A3B8]" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn">
      <h2 className="font-heading text-2xl font-bold text-[#0B1F3A]">Messages</h2>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-card">
          <h3 className="font-heading font-semibold text-[#0B1F3A] mb-4">Send Message to Client</h3>
          <form onSubmit={handleSend} className="space-y-4">
            <select value={form.recipient_id} onChange={(e) => setForm(f => ({ ...f, recipient_id: e.target.value }))} required className="w-full h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all" data-testid="message-recipient-select">
              <option value="">Select client...</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} ({c.email})</option>)}
            </select>
            <input value={form.subject} onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))} required placeholder="Subject" className="w-full h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all" />
            <textarea value={form.body} onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))} required rows={4} placeholder="Message..." className="w-full px-4 py-3 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] resize-none transition-all" />
            <button type="submit" disabled={sending} className="btn-primary text-sm px-6 py-2.5 disabled:opacity-60" data-testid="admin-send-message-btn">
              <Send size={16} /> {sending ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
          <div className="p-4 border-b border-gray-100"><h3 className="font-heading font-semibold text-[#0B1F3A]">Inbox ({messages.length})</h3></div>
          <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
            {messages.length === 0 ? (
              <div className="p-8 text-center text-[#94A3B8] text-sm">
                <MessageSquare size={32} className="mx-auto mb-2 opacity-30" />
                No messages yet
              </div>
            ) : messages.map((msg) => (
              <div key={msg.id} className={`p-4 hover:bg-[#F8FAFC] ${!msg.is_read && msg.sender_role !== 'admin' ? 'bg-blue-50' : ''}`} data-testid="admin-message-row">
                <div className="flex items-start justify-between mb-1">
                  <div className="font-medium text-[#0B1F3A] text-sm">{msg.subject}</div>
                  {!msg.is_read && msg.sender_role !== 'admin' && (
                    <button onClick={() => markRead(msg.id)} className="text-xs text-[#2563EB] hover:underline flex-shrink-0">Mark read</button>
                  )}
                </div>
                <div className="text-[#475569] text-xs mb-1">From: {msg.sender_name} ({msg.sender_role})</div>
                <div className="text-[#475569] text-xs line-clamp-2">{msg.body}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Blog CMS ────────────────────────────────────────────
const BlogTab = ({ API, headers }) => {
  const [posts, setPosts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', excerpt: '', content: '', category: '', tags: '', featured_image: '', is_published: false });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    const res = await axios.get(`${API}/blog/posts/all`, { headers });
    setPosts(res.data || []);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) };
      if (editing) {
        await axios.put(`${API}/blog/posts/${editing}`, data, { headers });
      } else {
        await axios.post(`${API}/blog/posts`, data, { headers });
      }
      setShowForm(false);
      setEditing(null);
      setForm({ title: '', excerpt: '', content: '', category: '', tags: '', featured_image: '', is_published: false });
      fetchPosts();
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (post) => {
    setEditing(post.id);
    setForm({ ...post, tags: (post.tags || []).join(', ') });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    await axios.delete(`${API}/blog/posts/${id}`, { headers });
    fetchPosts();
  };

  const togglePublish = async (post) => {
    await axios.put(`${API}/blog/posts/${post.id}`, { is_published: !post.is_published }, { headers });
    fetchPosts();
  };

  return (
    <div className="space-y-4 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold text-[#0B1F3A]">Blog CMS</h2>
        <button onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ title: '', excerpt: '', content: '', category: '', tags: '', featured_image: '', is_published: false }); }} className="btn-primary text-sm px-5 py-2.5" data-testid="create-post-btn">
          <Plus size={16} /> New Post
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-card animate-fadeIn">
          <h3 className="font-heading font-semibold text-[#0B1F3A] mb-5">{editing ? 'Edit Post' : 'New Post'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Title *</label>
                <input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} required className="w-full h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all" data-testid="post-title-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Category</label>
                <input value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. Tax Tips" className="w-full h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Tags (comma separated)</label>
                <input value={form.tags} onChange={(e) => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="taxes, deductions, tips" className="w-full h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Excerpt *</label>
                <textarea value={form.excerpt} onChange={(e) => setForm(f => ({ ...f, excerpt: e.target.value }))} required rows={2} placeholder="Brief summary..." className="w-full px-4 py-3 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] resize-none transition-all" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Content (HTML) *</label>
                <textarea value={form.content} onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))} required rows={8} placeholder="<h2>Section</h2><p>Content...</p>" className="w-full px-4 py-3 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm font-mono outline-none focus:border-[#0B1F3A] resize-y transition-all" data-testid="post-content-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Featured Image URL</label>
                <input value={form.featured_image} onChange={(e) => setForm(f => ({ ...f, featured_image: e.target.value }))} placeholder="https://..." className="w-full h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all" />
              </div>
              <div className="flex items-center gap-3 pt-5">
                <input type="checkbox" id="is_published" checked={form.is_published} onChange={(e) => setForm(f => ({ ...f, is_published: e.target.checked }))} className="w-5 h-5 accent-[#0B1F3A]" />
                <label htmlFor="is_published" className="text-sm font-medium text-[#0B1F3A]">Publish immediately</label>
              </div>
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={submitting} className="btn-primary text-sm px-6 py-2.5 disabled:opacity-60" data-testid="submit-post-btn">
                {submitting ? 'Saving...' : editing ? 'Save Changes' : 'Create Post'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2.5 rounded-xl border border-gray-200 text-sm text-[#475569]">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-3">
        {posts.map((post) => (
          <div key={post.id} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-card flex items-center justify-between" data-testid="blog-post-row">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <div className="font-semibold text-[#0B1F3A] truncate">{post.title}</div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${post.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                  {post.is_published ? 'Published' : 'Draft'}
                </span>
              </div>
              <div className="text-[#94A3B8] text-xs">{post.category} · {post.author}</div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 ml-4">
              <button onClick={() => togglePublish(post)} className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${post.is_published ? 'border-red-200 text-red-500 hover:bg-red-50' : 'border-green-200 text-green-600 hover:bg-green-50'}`} data-testid="toggle-publish-btn">
                {post.is_published ? 'Unpublish' : 'Publish'}
              </button>
              <button onClick={() => handleEdit(post)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-testid="edit-post-btn"><Edit size={16} className="text-[#475569]" /></button>
              <button onClick={() => handleDelete(post.id)} className="p-2 hover:bg-red-50 rounded-lg transition-colors" data-testid="delete-post-btn"><Trash2 size={16} className="text-red-400" /></button>
            </div>
          </div>
        ))}
        {posts.length === 0 && <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-card text-[#94A3B8]">No blog posts yet.</div>}
      </div>
    </div>
  );
};

// ─── Payment Requests Tab ─────────────────────────────────────────────────────
const PaymentRequestsTab = ({ API, headers }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    service_description: '',
    amount: '',
    currency: 'usd',
    payment_method: 'checkout',
    internal_notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadRequests();
  }, [filter]);

  const loadRequests = async () => {
    try {
      const url = filter === 'all' 
        ? `${API}/stripe/payment-requests`
        : `${API}/stripe/payment-requests?status=${filter}`;
      const res = await axios.get(url, { headers });
      setRequests(res.data);
    } catch (err) {
      console.error('Failed to load payment requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/stripe/payment-request`, {
        ...formData,
        amount: parseFloat(formData.amount)
      }, { headers });
      setShowForm(false);
      setFormData({ client_name: '', client_email: '', client_phone: '', service_description: '', amount: '', currency: 'usd', payment_method: 'checkout', internal_notes: '' });
      loadRequests();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create payment request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSend = async (id) => {
    if (!window.confirm('Send this payment request to the client?')) return;
    try {
      const res = await axios.post(`${API}/stripe/payment-request/${id}/send`, {}, { headers });
      alert(res.data.message + (res.data.email_sent ? ' Email sent.' : ' Email logged (mock mode).'));
      loadRequests();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send');
    }
  };

  const handleResend = async (id) => {
    try {
      await axios.post(`${API}/stripe/payment-request/${id}/resend`, {}, { headers });
      alert('Reminder sent!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to resend');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-600',
      sent: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
      needs_review: 'bg-amber-100 text-amber-700',
      canceled: 'bg-gray-100 text-gray-500'
    };
    return `px-2.5 py-1 rounded-full text-xs font-semibold ${styles[status] || styles.draft}`;
  };

  if (loading) return <div className="text-center py-20"><Loader size={24} className="animate-spin mx-auto text-[#94A3B8]" /></div>;

  return (
    <div data-testid="payment-requests-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-heading font-bold text-xl text-[#0B1F3A]">Payment Requests</h2>
          <p className="text-[#94A3B8] text-sm">Create and manage custom payment requests</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="needs_review">Needs Review</option>
            <option value="failed">Failed</option>
          </select>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm text-white"
            style={{ background: '#0B1F3A' }}
            data-testid="create-payment-request-btn"
          >
            <Plus size={16} /> New Request
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-card mb-6">
          <h3 className="font-semibold text-[#0B1F3A] mb-4">Create Payment Request</h3>
          <form onSubmit={handleCreate} className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#475569] mb-1">Client Name *</label>
              <input
                type="text"
                value={formData.client_name}
                onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#475569] mb-1">Client Email *</label>
              <input
                type="email"
                value={formData.client_email}
                onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#475569] mb-1">Phone (Optional)</label>
              <input
                type="tel"
                value={formData.client_phone}
                onChange={(e) => setFormData({...formData, client_phone: e.target.value})}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#475569] mb-1">Amount (USD) *</label>
              <input
                type="number"
                step="0.01"
                min="1"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#475569] mb-1">Service Description *</label>
              <textarea
                value={formData.service_description}
                onChange={(e) => setFormData({...formData, service_description: e.target.value})}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm"
                rows={2}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#475569] mb-1">Payment Method</label>
              <select
                value={formData.payment_method}
                onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm"
              >
                <option value="checkout">Stripe Checkout (Pay Link)</option>
                <option value="invoice">Stripe Invoice</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#475569] mb-1">Internal Notes</label>
              <input
                type="text"
                value={formData.internal_notes}
                onChange={(e) => setFormData({...formData, internal_notes: e.target.value})}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm"
                placeholder="For your reference only"
              />
            </div>
            <div className="md:col-span-2 flex gap-3 pt-2">
              <button type="submit" disabled={submitting} className="btn-primary text-sm px-6 py-2.5 disabled:opacity-60">
                {submitting ? 'Creating...' : 'Create Request'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2.5 rounded-xl border border-gray-200 text-sm text-[#475569]">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-3">
        {requests.map((req) => (
          <div key={req.id} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-card" data-testid="payment-request-row">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-[#0B1F3A]">{req.client_name}</span>
                  <span className={getStatusBadge(req.status)}>{req.status.replace('_', ' ')}</span>
                </div>
                <div className="text-[#475569] text-sm">{req.client_email}</div>
                <div className="text-[#94A3B8] text-xs mt-1">{req.service_description}</div>
                <div className="text-xs text-[#94A3B8] mt-2">Ref: {req.reference_id}</div>
              </div>
              <div className="text-right">
                <div className="font-bold text-xl text-[#0B1F3A]">${req.amount.toLocaleString()}</div>
                <div className="text-xs text-[#94A3B8] uppercase">{req.currency}</div>
              </div>
            </div>
            {req.change_request && (
              <div className="mt-3 p-3 bg-amber-50 rounded-xl border border-amber-200">
                <div className="text-xs font-semibold text-amber-700 mb-1">Change Request</div>
                <div className="text-sm text-amber-800">{req.change_request.reason}: {req.change_request.message}</div>
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
              {req.status === 'draft' && (
                <button onClick={() => handleSend(req.id)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold">
                  <Send size={14} /> Send to Client
                </button>
              )}
              {req.status === 'sent' && (
                <button onClick={() => handleResend(req.id)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-[#475569]">
                  <RefreshCw size={14} /> Resend
                </button>
              )}
              {req.stripe_payment_url && (
                <a href={req.stripe_payment_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-[#475569]">
                  <ExternalLink size={14} /> View Pay Link
                </a>
              )}
            </div>
          </div>
        ))}
        {requests.length === 0 && (
          <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-card text-[#94A3B8]">
            No payment requests yet. Click "New Request" to create one.
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Email Logs Tab ─────────────────────────────────────────────────────
const EmailLogsTab = ({ API, headers }) => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, failed: 0, not_configured: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadLogs();
  }, [filter]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const statusParam = filter !== 'all' ? `&status=${filter}` : '';
      const res = await axios.get(`${API}/admin/email-logs?limit=100${statusParam}`, { headers });
      setLogs(res.data.logs || []);
      setStats(res.data.stats || { sent: 0, failed: 0, not_configured: 0 });
    } catch (err) {
      console.error('Failed to load email logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'sent': return 'bg-green-500';
      case 'failed': return 'bg-red-500';
      case 'not_configured': return 'bg-amber-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'sent': return 'bg-green-100 text-green-700';
      case 'failed': return 'bg-red-100 text-red-700';
      case 'not_configured': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) return <div className="text-center py-20"><Loader size={24} className="animate-spin mx-auto text-[#94A3B8]" /></div>;

  return (
    <div data-testid="email-logs-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading font-bold text-xl text-[#0B1F3A]">Email Logs</h2>
          <p className="text-[#94A3B8] text-sm">Track all email notifications</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="px-2 py-1 rounded bg-green-100 text-green-700 font-semibold">{stats.sent} Sent</span>
            <span className="px-2 py-1 rounded bg-red-100 text-red-700 font-semibold">{stats.failed} Failed</span>
            <span className="px-2 py-1 rounded bg-amber-100 text-amber-700 font-semibold">{stats.not_configured} Not Configured</span>
          </div>
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All Status</option>
            <option value="sent">Sent Only</option>
            <option value="failed">Failed Only</option>
            <option value="not_configured">Not Configured</option>
          </select>
          <button onClick={loadLogs} className="p-2 hover:bg-gray-100 rounded-lg"><RefreshCw size={18} className="text-[#475569]" /></button>
        </div>
      </div>

      {selectedLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedLog(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#0B1F3A]">Email Details</h3>
              <button onClick={() => setSelectedLog(null)} className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
            </div>
            <div className="space-y-3 text-sm">
              <div><span className="text-[#94A3B8]">Recipient:</span> <span className="font-medium">{selectedLog.recipient}</span></div>
              <div><span className="text-[#94A3B8]">Subject:</span> <span className="font-medium">{selectedLog.subject}</span></div>
              <div><span className="text-[#94A3B8]">Type:</span> <span className="font-medium">{selectedLog.email_type}</span></div>
              <div><span className="text-[#94A3B8]">Status:</span> <span className={`font-semibold px-2 py-0.5 rounded ${getStatusBadge(selectedLog.status)}`}>{selectedLog.status}</span></div>
              <div><span className="text-[#94A3B8]">Date:</span> {new Date(selectedLog.created_at).toLocaleString()}</div>
              {selectedLog.error_message && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <span className="text-red-600 font-medium">Error:</span> <span className="text-red-700">{selectedLog.error_message}</span>
                </div>
              )}
              {selectedLog.html_preview && (
                <div className="mt-4">
                  <div className="text-[#94A3B8] mb-2">Preview:</div>
                  <div className="border rounded-xl p-4 bg-gray-50 max-h-64 overflow-auto text-xs" dangerouslySetInnerHTML={{ __html: selectedLog.html_preview }} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {logs.map((log) => (
          <div key={log.id} className="bg-white rounded-xl p-4 border border-gray-100 flex items-center justify-between" data-testid="email-log-row">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusColor(log.status)}`} />
              <div className="min-w-0">
                <div className="font-medium text-[#0B1F3A] text-sm truncate">{log.subject}</div>
                <div className="text-xs text-[#94A3B8]">To: {log.recipient} · Type: {log.email_type}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(log.status)}`}>
                {log.status === 'not_configured' ? 'No SMTP' : log.status}
              </span>
              <span className="text-xs text-[#94A3B8]">{new Date(log.created_at).toLocaleDateString()}</span>
              <button onClick={() => setSelectedLog(log)} className="p-1.5 hover:bg-gray-100 rounded"><Eye size={16} className="text-[#475569]" /></button>
            </div>
          </div>
        ))}
        {logs.length === 0 && (
          <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-card text-[#94A3B8]">
            No email logs yet. Emails will be logged here when notifications are triggered.
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Security (2FA) Tab ─────────────────────────────────────────────────────
const SecurityTab = ({ API, headers }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [smsLogs, setSmsLogs] = useState([]);
  const [showSmsLogs, setShowSmsLogs] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/2fa/admin/users`, { headers });
      setUsers(res.data);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSmsLogs = async () => {
    try {
      const res = await axios.get(`${API}/2fa/admin/sms-logs`, { headers });
      setSmsLogs(res.data);
      setShowSmsLogs(true);
    } catch (err) {
      console.error('Failed to load SMS logs:', err);
    }
  };

  const disable2FA = async (userId, email) => {
    if (!window.confirm(`Disable 2FA for ${email}? They will need to re-enroll.`)) return;
    try {
      await axios.post(`${API}/2fa/admin/disable/${userId}`, {}, { headers });
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to disable 2FA');
    }
  };

  if (loading) return <div className="text-center py-20"><Loader size={24} className="animate-spin mx-auto text-[#94A3B8]" /></div>;

  const enabled2FA = users.filter(u => u.two_factor_enabled).length;

  return (
    <div data-testid="security-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading font-bold text-xl text-[#0B1F3A]">2FA & Security</h2>
          <p className="text-[#94A3B8] text-sm">Manage user two-factor authentication</p>
        </div>
        <button onClick={loadSmsLogs} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-[#475569]">
          <MessageSquare size={16} /> SMS Logs
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="text-2xl font-bold text-[#0B1F3A]">{users.length}</div>
          <div className="text-sm text-[#94A3B8]">Total Users</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="text-2xl font-bold text-green-600">{enabled2FA}</div>
          <div className="text-sm text-[#94A3B8]">2FA Enabled</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="text-2xl font-bold text-amber-600">{users.length - enabled2FA}</div>
          <div className="text-sm text-[#94A3B8]">Pending Enrollment</div>
        </div>
      </div>

      {showSmsLogs && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowSmsLogs(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-xl w-full max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#0B1F3A]">SMS OTP Logs</h3>
              <button onClick={() => setShowSmsLogs(false)} className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
            </div>
            <div className="space-y-2">
              {smsLogs.map((log, i) => (
                <div key={i} className="p-3 bg-gray-50 rounded-lg text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{log.phone}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${log.is_mock ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                      {log.is_mock ? 'Mock' : log.status}
                    </span>
                  </div>
                  {log.otp && <div className="text-amber-600 font-mono mt-1">OTP: {log.otp}</div>}
                  <div className="text-[#94A3B8] text-xs mt-1">{new Date(log.created_at).toLocaleString()}</div>
                </div>
              ))}
              {smsLogs.length === 0 && <div className="text-center text-[#94A3B8] py-8">No SMS logs yet</div>}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-[#475569] font-semibold">User</th>
              <th className="px-4 py-3 text-left text-[#475569] font-semibold">Role</th>
              <th className="px-4 py-3 text-center text-[#475569] font-semibold">2FA Status</th>
              <th className="px-4 py-3 text-center text-[#475569] font-semibold">Backup Codes</th>
              <th className="px-4 py-3 text-right text-[#475569] font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.map((user) => (
              <tr key={user.id} data-testid="security-user-row">
                <td className="px-4 py-3">
                  <div className="font-medium text-[#0B1F3A]">{user.first_name} {user.last_name}</div>
                  <div className="text-[#94A3B8] text-xs">{user.email}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                    {user.role}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  {user.two_factor_enabled ? (
                    <span className="inline-flex items-center gap-1 text-green-600"><Check size={16} /> Enabled</span>
                  ) : (
                    <span className="text-amber-600">Not Enrolled</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center text-[#475569]">{user.backup_codes_remaining || 0}</td>
                <td className="px-4 py-3 text-right">
                  {user.two_factor_enabled && (
                    <button onClick={() => disable2FA(user.id, user.email)} className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50">
                      Disable 2FA
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Config Status Tab ─────────────────────────────────────────────────────
const ConfigStatusTab = ({ API, headers }) => {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const [configRes, statsRes] = await Promise.all([
        axios.get(`${API}/config/status`, { headers }),
        axios.get(`${API}/config/stats`, { headers })
      ]);
      setConfig(configRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to load config:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleLiveMode = async () => {
    try {
      const res = await axios.post(`${API}/config/live-mode`, {}, { headers });
      alert(res.data.message);
      loadConfig();
    } catch (err) {
      alert(err.response?.data?.detail || 'Cannot enable live mode');
    }
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(config?.integrations?.stripe?.webhook_endpoint || '');
    alert('Webhook URL copied!');
  };

  if (loading) return <div className="text-center py-20"><Loader size={24} className="animate-spin mx-auto text-[#94A3B8]" /></div>;

  return (
    <div data-testid="config-status-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading font-bold text-xl text-[#0B1F3A]">System Configuration</h2>
          <p className="text-[#94A3B8] text-sm">Integration status and live mode toggle</p>
        </div>
        <button onClick={loadConfig} className="p-2 hover:bg-gray-100 rounded-lg"><RefreshCw size={18} className="text-[#475569]" /></button>
      </div>

      {/* Live Mode Toggle */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-card mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-[#0B1F3A] text-lg">Live Mode</h3>
            <p className="text-[#94A3B8] text-sm">
              {config?.live_mode?.can_enable 
                ? 'All requirements met. You can enable live mode.'
                : 'Configure all integrations to enable live mode.'}
            </p>
          </div>
          <button
            onClick={toggleLiveMode}
            disabled={!config?.live_mode?.can_enable}
            className={`px-6 py-3 rounded-xl font-semibold ${
              config?.live_mode?.enabled 
                ? 'bg-green-600 text-white' 
                : config?.live_mode?.can_enable 
                  ? 'bg-[#0B1F3A] text-white' 
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {config?.live_mode?.enabled ? '✓ Live Mode Active' : 'Enable Live Mode'}
          </button>
        </div>
      </div>

      {/* Integration Status Cards */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {/* Stripe */}
        <div className={`bg-white rounded-2xl p-5 border-2 ${config?.integrations?.stripe?.configured ? 'border-green-200' : 'border-amber-200'}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${config?.integrations?.stripe?.configured ? 'bg-green-100' : 'bg-amber-100'}`}>
              <CreditCard size={20} className={config?.integrations?.stripe?.configured ? 'text-green-600' : 'text-amber-600'} />
            </div>
            <div>
              <div className="font-semibold text-[#0B1F3A]">Stripe</div>
              <div className={`text-xs font-semibold ${config?.integrations?.stripe?.configured ? 'text-green-600' : 'text-amber-600'}`}>
                {config?.integrations?.stripe?.status?.toUpperCase()}
              </div>
            </div>
          </div>
          <p className="text-sm text-[#475569] mb-3">{config?.integrations?.stripe?.message}</p>
          {config?.integrations?.stripe?.missing?.length > 0 && (
            <div className="text-xs text-red-600">Missing: {config?.integrations?.stripe?.missing?.join(', ')}</div>
          )}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="text-xs text-[#94A3B8] mb-1">Webhook Endpoint:</div>
            <div className="flex items-center gap-2">
              <code className="text-xs bg-gray-100 px-2 py-1 rounded flex-1 truncate">{config?.integrations?.stripe?.webhook_endpoint}</code>
              <button onClick={copyWebhook} className="p-1 hover:bg-gray-100 rounded"><Copy size={14} /></button>
            </div>
          </div>
        </div>

        {/* Gmail */}
        <div className={`bg-white rounded-2xl p-5 border-2 ${config?.integrations?.gmail?.configured ? 'border-green-200' : 'border-amber-200'}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${config?.integrations?.gmail?.configured ? 'bg-green-100' : 'bg-amber-100'}`}>
              <Mail size={20} className={config?.integrations?.gmail?.configured ? 'text-green-600' : 'text-amber-600'} />
            </div>
            <div>
              <div className="font-semibold text-[#0B1F3A]">Gmail SMTP</div>
              <div className={`text-xs font-semibold ${config?.integrations?.gmail?.configured ? 'text-green-600' : 'text-amber-600'}`}>
                {config?.integrations?.gmail?.status?.toUpperCase()}
              </div>
            </div>
          </div>
          <p className="text-sm text-[#475569] mb-3">{config?.integrations?.gmail?.message}</p>
          {config?.integrations?.gmail?.missing?.length > 0 && (
            <div className="text-xs text-red-600">Missing: {config?.integrations?.gmail?.missing?.join(', ')}</div>
          )}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="text-xs text-[#94A3B8]">From: {config?.integrations?.gmail?.from_email}</div>
          </div>
        </div>

        {/* Twilio */}
        <div className={`bg-white rounded-2xl p-5 border-2 ${config?.integrations?.twilio?.configured ? 'border-green-200' : 'border-amber-200'}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${config?.integrations?.twilio?.configured ? 'bg-green-100' : 'bg-amber-100'}`}>
              <MessageSquare size={20} className={config?.integrations?.twilio?.configured ? 'text-green-600' : 'text-amber-600'} />
            </div>
            <div>
              <div className="font-semibold text-[#0B1F3A]">Twilio SMS</div>
              <div className={`text-xs font-semibold ${config?.integrations?.twilio?.configured ? 'text-green-600' : 'text-amber-600'}`}>
                {config?.integrations?.twilio?.status?.toUpperCase()}
              </div>
            </div>
          </div>
          <p className="text-sm text-[#475569] mb-3">{config?.integrations?.twilio?.message}</p>
          {config?.integrations?.twilio?.missing?.length > 0 && (
            <div className="text-xs text-red-600">Missing: {config?.integrations?.twilio?.missing?.join(', ')}</div>
          )}
        </div>
      </div>

      {/* System Stats */}
      {stats && (
        <div className="bg-white rounded-2xl p-6 border border-gray-100">
          <h3 className="font-semibold text-[#0B1F3A] mb-4">System Statistics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 bg-gray-50 rounded-xl">
              <div className="text-2xl font-bold text-[#0B1F3A]">{stats.users?.total || 0}</div>
              <div className="text-xs text-[#94A3B8]">Total Users</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-xl">
              <div className="text-2xl font-bold text-green-600">{stats.payments?.paid || 0}</div>
              <div className="text-xs text-[#94A3B8]">Payments Received</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-xl">
              <div className="text-2xl font-bold text-blue-600">{stats.emails?.total || 0}</div>
              <div className="text-xs text-[#94A3B8]">Emails ({stats.emails?.sent || 0} sent, {stats.emails?.mocked || 0} mocked)</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-xl">
              <div className="text-2xl font-bold text-purple-600">{stats.security?.percent_2fa || 0}%</div>
              <div className="text-xs text-[#94A3B8]">Users with 2FA</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Revenue Analytics Tab ─────────────────────────────────────────────────────
const RevenueAnalyticsTab = ({ API, headers }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(12);

  useEffect(() => {
    loadData();
  }, [months]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/revenue-analytics?months=${months}`, { headers });
      setData(res.data);
    } catch (err) {
      console.error('Failed to load revenue analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-20"><Loader size={24} className="animate-spin mx-auto text-[#94A3B8]" /></div>;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Calculate max for chart scaling
  const maxMonthly = Math.max(...(data?.monthly_data || []).map(m => m.total || 0), 1);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="revenue-analytics-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#0B1F3A]">Revenue Analytics</h2>
          <p className="text-[#475569] text-sm">Track your business revenue and growth</p>
        </div>
        <div className="flex items-center gap-3">
          <select 
            value={months} 
            onChange={(e) => setMonths(Number(e.target.value))}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm"
          >
            <option value={6}>Last 6 Months</option>
            <option value={12}>Last 12 Months</option>
            <option value={24}>Last 24 Months</option>
          </select>
          <button onClick={loadData} className="p-2 hover:bg-gray-100 rounded-lg">
            <RefreshCw size={18} className="text-[#475569]" />
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D4AF37]/15">
              <DollarSign size={20} className="text-[#D4AF37]" />
            </div>
            <div className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wide">Total Revenue</div>
          </div>
          <div className="font-heading font-bold text-3xl text-[#0B1F3A]">{formatCurrency(data?.total_revenue)}</div>
          <div className="text-xs text-[#94A3B8] mt-1">{data?.total_transactions || 0} transactions</div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-100">
              <TrendingUp size={20} className="text-blue-600" />
            </div>
            <div className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wide">This Month</div>
          </div>
          <div className="font-heading font-bold text-3xl text-[#0B1F3A]">{formatCurrency(data?.current_month_revenue)}</div>
          <div className={`text-xs mt-1 flex items-center gap-1 ${data?.growth_percent >= 0 ? 'text-green-600' : 'text-red-500'}`}>
            {data?.growth_percent >= 0 ? '↑' : '↓'} {Math.abs(data?.growth_percent || 0).toFixed(1)}% vs last month
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-100">
              <BarChart2 size={20} className="text-green-600" />
            </div>
            <div className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wide">Avg Monthly</div>
          </div>
          <div className="font-heading font-bold text-3xl text-[#0B1F3A]">{formatCurrency(data?.average_monthly)}</div>
          <div className="text-xs text-[#94A3B8] mt-1">Based on {months} months</div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-purple-100">
              <Calendar size={20} className="text-purple-600" />
            </div>
            <div className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wide">Last Month</div>
          </div>
          <div className="font-heading font-bold text-3xl text-[#0B1F3A]">{formatCurrency(data?.previous_month_revenue)}</div>
          <div className="text-xs text-[#94A3B8] mt-1">Comparison baseline</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Monthly Revenue Chart */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-card">
          <h3 className="font-heading font-semibold text-[#0B1F3A] mb-4">Monthly Revenue</h3>
          <div className="space-y-3">
            {(data?.monthly_data || []).slice().reverse().map((month) => (
              <div key={month.month} className="flex items-center gap-3">
                <div className="w-20 text-xs text-[#94A3B8] font-medium">{month.month}</div>
                <div className="flex-1 h-8 bg-[#F8FAFC] rounded-lg overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-[#D4AF37] to-[#0B1F3A] rounded-lg transition-all duration-500"
                    style={{ width: `${Math.max((month.total || 0) / maxMonthly * 100, 2)}%` }}
                  />
                </div>
                <div className="w-24 text-right font-semibold text-[#0B1F3A] text-sm">
                  {formatCurrency(month.total)}
                </div>
              </div>
            ))}
            {(!data?.monthly_data || data.monthly_data.length === 0) && (
              <div className="text-center text-[#94A3B8] py-8">
                <DollarSign size={32} className="mx-auto mb-2 opacity-30" />
                No revenue data yet
              </div>
            )}
          </div>
        </div>

        {/* Revenue by Service */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-card">
          <h3 className="font-heading font-semibold text-[#0B1F3A] mb-4">Revenue by Service Type</h3>
          <div className="space-y-3">
            {Object.entries(data?.by_service || {}).sort((a, b) => b[1] - a[1]).map(([service, amount]) => {
              const percent = data?.total_revenue ? (amount / data.total_revenue * 100) : 0;
              return (
                <div key={service} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-[#0B1F3A]">{service}</span>
                      <span className="text-sm font-semibold text-[#0B1F3A]">{formatCurrency(amount)}</span>
                    </div>
                    <div className="h-2 bg-[#F8FAFC] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#0B1F3A] rounded-full transition-all duration-500"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <div className="text-xs text-[#94A3B8] mt-1">{percent.toFixed(1)}% of total</div>
                  </div>
                </div>
              );
            })}
            {Object.keys(data?.by_service || {}).length === 0 && (
              <div className="text-center text-[#94A3B8] py-8">
                <BarChart2 size={32} className="mx-auto mb-2 opacity-30" />
                No service breakdown available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Transactions Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          <h3 className="font-heading font-semibold text-[#0B1F3A]">Recent Transactions</h3>
        </div>
        {(data?.recent_transactions || []).length === 0 ? (
          <div className="p-12 text-center text-[#94A3B8]">
            <Receipt size={40} className="mx-auto mb-3 opacity-30" />
            <p>No transactions yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Client</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Service</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Date</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(data?.recent_transactions || []).map((tx, i) => (
                  <tr key={i} className="hover:bg-[#F8FAFC]" data-testid="revenue-transaction-row">
                    <td className="px-4 py-3 font-medium text-[#0B1F3A]">{tx.client_name}</td>
                    <td className="px-4 py-3 text-[#475569]">{tx.service}</td>
                    <td className="px-4 py-3 text-[#94A3B8]">{tx.date}</td>
                    <td className="px-4 py-3 text-right font-semibold text-green-600">{formatCurrency(tx.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
